import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, map, Observable, of, throwError } from 'rxjs';
import { switchMap, catchError, delay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  MonthlyRevenue,
  SalesFunnel,
  FunnelStage,
  MonthlyLeads,
  MyAgenda,
  AgendaTask,
  AgendaTaskType,
  DashboardParams,
  DealCycleResponse,
  TicketsByStatus,
  BusinessesByStatus,
  EmailFunnel,
  MeetingsPivot,
  MeetingsPivotRow,
  BusinessStatusSlice,
  MeetingsTopItem,
  EmailFunnelStage,
  EmailCampaignsByStatus,
  EmailKeyMetrics
} from '../models/dashboard.model';
import { PageResponse } from '../../auth-pages/models/user.model';
import { DealResponse } from '../../deals/models/deal.model';
import { MarketingCampaignApiService } from '../../marketing-email/services/marketing-campaign-api.service';
import { CampaignMetrics, MarketingCampaign } from '../../marketing-email/models/marketing-campaign.model';
import { TicketsApiService } from '../../tickets/services/tickets-api.service';
import { TicketMainDashboardResponse } from '../../tickets/models/tickets.model';
import { QuoteApiService } from '../../quotes/services/quote-api.service';
import { QuoteMetricsResponse } from '../../quotes/models/quote.model';


@Injectable({
  providedIn: 'root'
})
export class DashboardApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private ticketsApi = inject(TicketsApiService);
  private marketingApi = inject(MarketingCampaignApiService);
  private quoteApi = inject(QuoteApiService);

  private readonly apiUrl = `${environment.apiUrl}api`;

  private readonly useEmailMocks  = false;
  private readonly mockDelayMs = 400;
  

  // ============================================================
  // RF-04, RF-05 — Ingresos del mes (negocios ganados, mes actual vs anterior)
  // ============================================================
  getMonthlyRevenue(params: DashboardParams): Observable<MonthlyRevenue> {
    const { currentFrom, currentTo, prevFrom, prevTo } = this.getMonthRanges();

    // Necesitamos: deals ganados del mes actual, deals ganados del mes anterior,
    // y los deal-cycles para identificar cuáles son "ganados".
    return forkJoin({
      cycles: this.getDealCyclesRaw(),
      currentDeals: this.fetchClosedDeals(params.businessId, currentFrom, currentTo),
      previousDeals: this.fetchClosedDeals(params.businessId, prevFrom, prevTo)
    }).pipe(
      map(({ cycles, currentDeals, previousDeals }) => {
        const wonCycleIds = this.identifyWonCycleIds(cycles);
        const currentTotal = this.sumWonAmounts(currentDeals, wonCycleIds);
        const previousTotal = this.sumWonAmounts(previousDeals, wonCycleIds);
        const variation = previousTotal > 0
          ? ((currentTotal - previousTotal) / previousTotal) * 100
          : (currentTotal > 0 ? 100 : 0);

        return {
          businessId: params.businessId,
          currentMonthTotal: currentTotal,
          previousMonthTotal: previousTotal,
          variationPercent: Number(variation.toFixed(2)),
          currency: 'PEN',                  // TODO: leer de business config
          periodFrom: currentFrom.split('T')[0],
          periodTo: currentTo.split('T')[0]
        } as MonthlyRevenue;
      })
    );
  }

  // ============================================================
  // RF-06, RF-07 — Embudo de ventas (deals activos agrupados por etapa)
  // ============================================================
  getSalesFunnel(params: DashboardParams): Observable<SalesFunnel> {
    return forkJoin({
      cycles: this.getDealCyclesRaw(),
      deals: this.fetchAllPagesOfDeals(params.businessId, { activeOnly: true })
    }).pipe(
      map(({ cycles, deals }) => {
        // Agrupar deals por idDealCycle
        const byCycle = new Map<number, { count: number; total: number }>();
        for (const deal of deals) {
          if (!deal.idDealCycle) continue;
          const acc = byCycle.get(deal.idDealCycle) ?? { count: 0, total: 0 };
          acc.count += 1;
          acc.total += this.dealAmount(deal);
          byCycle.set(deal.idDealCycle, acc);
        }

        // Excluir las etapas terminales (ganado/perdido) del embudo activo
        const wonOrLost = new Set([
          ...this.identifyWonCycleIds(cycles),
          ...this.identifyLostCycleIds(cycles)
        ]);

        const stages: FunnelStage[] = cycles
          .filter(c => !wonOrLost.has(c.id))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))           // ← order, no displayOrder
          .map(c => {
            const data = byCycle.get(c.id) ?? { count: 0, total: 0 };
            return {
              stageId: c.id,
              stageName: c.name,
              displayOrder: c.order ?? 0,                            // ← mapear order → displayOrder
              dealsCount: data.count,
              totalAmount: data.total,
              currency: 'PEN'
            };
          });

        return {
          businessId: params.businessId,
          stages,
          totalPipelineAmount: stages.reduce((sum, s) => sum + s.totalAmount, 0),
          currency: 'PEN'
        } as SalesFunnel;
      })
    );
  }

  // ============================================================
  // RF-08, RF-09 — Leads del mes (PAUSADO: backend sin filtro de fecha)
  // ============================================================
  getMonthlyLeads(params: DashboardParams): Observable<MonthlyLeads> {
  const { currentFrom, currentTo } = this.getMonthRanges();
  const fromDate = new Date(currentFrom);
  const toDate = new Date(currentTo);

  return forkJoin({
    contacts: this.fetchAllContacts(params.businessId),
    origins: this.fetchContactOrigins(params.businessId)
  }).pipe(
    map(({ contacts, origins }) => {
      const originNameById = new Map<number, string>(
        origins.map(o => [o.id, o.name])
      );

      // RF-08: contador SOLO del mes actual
      const monthlyContactsCount = contacts.filter(c => {
        if (!c.datetimeCreate) return false;
        const created = new Date(c.datetimeCreate);
        return created >= fromDate && created <= toDate;
      }).length;

      // RF-09: desglose por fuente sobre TODOS los contactos
      const counts = new Map<number, number>();
      let withoutOrigin = 0;

      for (const c of contacts) {
        if (c.idOrigin != null) {
          counts.set(c.idOrigin, (counts.get(c.idOrigin) ?? 0) + 1);
        } else {
          withoutOrigin++;
        }
      }

      const bySource = Array.from(counts.entries())
        .map(([originId, count]) => ({
          originId,
          originName: originNameById.get(originId) ?? `Origen ${originId}`,
          count
        }))
        .sort((a, b) => b.count - a.count);

      if (withoutOrigin > 0) {
        bySource.push({
          originId: 0,
          originName: 'Sin origen',
          count: withoutOrigin
        });
      }

      return {
        businessId: params.businessId,
        totalLeads: monthlyContactsCount,    
        bySource,                            
        periodFrom: currentFrom.split('T')[0],
        periodTo: currentTo.split('T')[0]
      } as MonthlyLeads;
    }),
    catchError(err => {
      console.warn('[Dashboard] Error al cargar contactos', err);
      return of({
        businessId: params.businessId,
        totalLeads: 0,
        bySource: [],
        periodFrom: currentFrom.split('T')[0],
        periodTo: currentTo.split('T')[0]
      } as MonthlyLeads);
    })
  );
}

  /** GET /api/contacts — trae todos los contactos del business (paginación grande). */
private fetchAllContacts(businessId: number): Observable<any[]> {
  const params = new HttpParams()
    .set('businessId', businessId.toString())
    .set('page', '0')
    .set('size', '500');

  return this.http.get<PageResponse<any>>(`${this.apiUrl}/contacts`, { params })
    .pipe(map(r => r.content ?? []));
}

/** GET /api/contact-origins — catálogo de orígenes para resolver nombres. */
private fetchContactOrigins(businessId: number): Observable<{ id: number; name: string }[]> {
  const params = new HttpParams()
    .set('businessId', businessId.toString())
    .set('page', '0')
    .set('size', '100');

  return this.http.get<PageResponse<any>>(`${this.apiUrl}/contact-origins`, { params })
    .pipe(map(r => r.content ?? []));
}

  // ============================================================
  // RF-10, RF-11 — Mi Agenda (llamadas hoy + tareas atrasadas)
  // ============================================================
  getMyAgenda(params: DashboardParams): Observable<MyAgenda> {
    const userId = params.userPropietyId;
    if (!userId) {
      return throwError(() => new Error('userPropietyId requerido para Mi Agenda'));
    }

    const { todayFrom, todayTo } = this.getTodayRange();

    // Resolver dinámicamente el typeId de "Llamada", luego pedir las dos listas
    return this.getCallTaskTypeId().pipe(
      switchMap(callTypeId => forkJoin({
        callsToday: this.filterTasks({
          intIdUserOwner: userId,                    // ← nombre real del campo
          idTaskType: callTypeId ?? undefined,       // ← antes typeId
          dueFrom: todayFrom,
          dueTo: todayTo,
          isCompleted: false
        }),
        overdueTasks: this.filterTasks({
          intIdUserOwner: userId,                    // ← nombre real del campo
          isOverdue: true,
          isCompleted: false
        })
      })),
      map(({ callsToday, overdueTasks }) => ({
        businessId: params.businessId,
        userPropietyId: userId,
        callsToday: callsToday.map(t => this.mapTaskToAgendaTask(t, false)),
        overdueTasks: overdueTasks.map(t => this.mapTaskToAgendaTask(t, true))
      } as MyAgenda))
    );
  }

  // ============================================================
  // HELPERS — llamadas a endpoints existentes
  // ============================================================

  /** GET /api/deal-cycles — devuelve todas las etapas del pipeline. */
  private getDealCyclesRaw(): Observable<DealCycleResponse[]> {
    return this.http.get<DealCycleResponse[] | PageResponse<DealCycleResponse>>(
      `${this.apiUrl}/deal-cycles`
    ).pipe(
      map(res => Array.isArray(res) ? res : (res?.content ?? []))
    );
  }

  /**
   * Trae todos los deals cerrados en un rango de fechas.
   * Usa size grande (500) para evitar paginar; si el backend devuelve más,
   * se paginaría con fetchAllPagesOfDeals.
   */
  private fetchClosedDeals(
    businessId: number,
    closedDateFrom: string,
    closedDateTo: string
  ): Observable<DealResponse[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('closedOnly', 'true')
      .set('closedDateFrom', closedDateFrom)
      .set('closedDateTo', closedDateTo)
      .set('page', '0')
      .set('size', '500');

    return this.http.get<PageResponse<DealResponse>>(`${this.apiUrl}/deals`, { params })
      .pipe(
        map(r => r.content ?? []),
        catchError(err => {
          console.warn(
            '[Dashboard] /api/deals (closedOnly) falló — el widget de ingresos ' +
            'mostrará 0 hasta que se resuelva el bug de OffsetDateTime.',
            err
          );
          return of([]);
        })
      );
  }

  /**
   * Trae TODAS las páginas de deals con un filtro dado (para el embudo activo).
   * Limitado a 5 páginas de 200 = 1000 deals max.
   */
  private fetchAllPagesOfDeals(
    businessId: number,
    extra: { activeOnly?: boolean } = {}
  ): Observable<DealResponse[]> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '1000');
    if (extra.activeOnly) params = params.set('activeOnly', 'true');

    return this.http.get<PageResponse<DealResponse>>(`${this.apiUrl}/deals`, { params })
      .pipe(
        map(r => r.content ?? []),
        catchError(err => {
          // Bug conocido del backend: algunos deals tienen datetimeClosed
          // guardado como LocalDateTime en lugar de OffsetDateTime,
          // lo que causa 500 al deserializar. Devolvemos lista vacía
          // para que el embudo se muestre como "sin datos" en vez de error.
          console.warn(
            '[Dashboard] /api/deals (activeOnly) falló — probablemente el bug ' +
            'de OffsetDateTime en backend. El embudo se mostrará vacío.',
            err
          );
          return of([]);
        })
      );
    }

  /** GET /api/task-types/active y busca el de "llamada". */
  private getCallTaskTypeId(): Observable<number | null> {
    return this.http.get<any[]>(`${this.apiUrl}/task-types/active`).pipe(
      map(types => {
        const call = types.find(t => {
          const name = (t.name ?? '').toLowerCase();
          return name.includes('llamada') || name.includes('call');
        });
        return call?.id ?? null;
      }),
      catchError(() => of(null))
    );
  }

  /** POST /api/tasks/filter */
  private filterTasks(filter: any): Observable<any[]> {
    const body = {
      ...filter,
      page: 0,
      size: 50,
      sortBy: 'datetimeDue',
      sortDirection: 'ASC'
    };
    return this.http.post<PageResponse<any>>(`${this.apiUrl}/tasks/filter`, body)
      .pipe(map(r => r.content ?? []));
  }

  // ============================================================
  // HELPERS — lógica de negocio
  // ============================================================

  /** Identifica las etapas "ganadas" por nombre (Ganado / Won). */
  private identifyWonCycleIds(cycles: DealCycleResponse[]): Set<number> {
    return new Set(cycles
      .filter(c => {
        if (c.isWon === true) return true;
        const name = (c.name ?? '').toLowerCase();
        return name.includes('ganad') || name.includes('won');
      })
      .map(c => c.id));
  }

  /** Identifica etapas "perdidas" para excluirlas del embudo activo. */
  private identifyLostCycleIds(cycles: DealCycleResponse[]): Set<number> {
    return new Set(cycles
      .filter(c => {
        if (c.isLost === true) return true;
        const name = (c.name ?? '').toLowerCase();
        return name.includes('perdid') || name.includes('lost');
      })
      .map(c => c.id));
  }

  /** Suma el monto solo de los deals cuya etapa esté en wonCycleIds. */
  private sumWonAmounts(deals: DealResponse[], wonCycleIds: Set<number>): number {
    return deals
      .filter(d => d.idDealCycle != null && wonCycleIds.has(d.idDealCycle))
      .reduce((sum, d) => sum + this.dealAmount(d), 0);
  }

  /** Prioridad: amount > amountPerYear > amountPerMonth*12. */
  private dealAmount(d: DealResponse): number {
    if (d.amount && d.amount > 0) return d.amount;
    if (d.amountPerYear && d.amountPerYear > 0) return d.amountPerYear;
    if (d.amountPerMonth && d.amountPerMonth > 0) return d.amountPerMonth * 12;
    return 0;
  }

  /** Mapea TaskResponse del backend al AgendaTask del dashboard. */
  private mapTaskToAgendaTask(t: any, isOverdue: boolean): AgendaTask {
    // El backend devuelve idTaskType numérico pero NO el nombre del tipo.
    // Lo dejamos como TASK genérico hasta que se exponga el nombre.
    const agendaType: AgendaTaskType = 'TASK';

    return {
      id: t.id,
      title: t.title ?? '',
      type: agendaType,
      dueDatetime: t.datetimeDue ?? new Date().toISOString(),
      contactId: undefined,                          // backend no lo expone en TaskResponse
      contactName: undefined,
      companyId: t.intIdCompany ?? undefined,
      companyName: undefined,
      isOverdue
    };
  }

  // ============================================================
  // HELPERS — fechas (ISO 8601 con offset Lima -05:00)
  // ============================================================

  private getMonthRanges() {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    return {
      currentFrom: this.toIsoOffset(new Date(y, m, 1, 0, 0, 0)),
      currentTo: this.toIsoOffset(new Date(y, m + 1, 0, 23, 59, 59)),
      prevFrom: this.toIsoOffset(new Date(y, m - 1, 1, 0, 0, 0)),
      prevTo: this.toIsoOffset(new Date(y, m, 0, 23, 59, 59))
    };
  }

  private getTodayRange() {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    return {
      todayFrom: this.toIsoOffset(new Date(y, m, d, 0, 0, 0)),
      todayTo: this.toIsoOffset(new Date(y, m, d, 23, 59, 59))
    };
  }

  /** Formato ISO 8601 con offset, ej: "2026-05-07T00:00:00-05:00" */
  private toIsoOffset(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const offH = pad(Math.floor(Math.abs(offsetMin) / 60));
    const offM = pad(Math.abs(offsetMin) % 60);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
           `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
           `${sign}${offH}:${offM}`;
  }

  //4 modulos mas

  getTicketsByStatus(params: DashboardParams): Observable<TicketsByStatus> {
    const httpParams = new HttpParams().set('page', '0').set('size', '500');

    return forkJoin({
      tickets: this.http.get<PageResponse<any>>(`${this.apiUrl}/tickets`, { params: httpParams }).pipe(
        map(r => r.content ?? []),
        catchError(() => of([] as any[]))
      ),
      dashboard: this.ticketsApi.getReportsDashboard().pipe(
        catchError(() => of(null as TicketMainDashboardResponse | null))
      )
    }).pipe(
      map(({ tickets, dashboard }) => this.buildTicketsByStatus(tickets, dashboard)),
      catchError(err => {
        console.warn('[Dashboard] /api/tickets falló — widget mostrará vacío', err);
        return of(this.buildTicketsByStatus([], null));
      })
    );
  }

  // ============================================================
  // Cotizaciones — KPIs comerciales (GET /api/quotes/metrics, requiere Manager+)
  // No se captura el error aquí a propósito: el componente necesita distinguir
  // un 403 (sin permiso → ocultar widget) de un error real.
  // ============================================================
  getQuoteMetrics(params: DashboardParams): Observable<QuoteMetricsResponse> {
    return this.quoteApi.getMetrics(params.businessId);
  }

  // ============================================================
  // Negocios por estado (RF-XX) — Dona
  // Reutiliza /api/deals + /api/deal-cycles que ya conocemos
  // ============================================================
  getBusinessesByStatus(params: DashboardParams): Observable<BusinessesByStatus> {
    return forkJoin({
      cycles: this.getDealCyclesRaw(),
      deals: this.fetchAllPagesOfDeals(params.businessId, { activeOnly: false })
    }).pipe(
      map(({ cycles, deals }) => this.buildBusinessesByStatus(cycles, deals))
    );
  }

  // ============================================================
  // Embudo de Correos (RF-XX) — MOCK temporal
  // ============================================================
  getEmailFunnel(params: DashboardParams): Observable<EmailFunnel> {
    if (this.useEmailMocks) {
      return of(this.mockEmailFunnel(params.businessId)).pipe(delay(this.mockDelayMs));
    }

    const { currentFrom, currentTo } = this.getMonthRanges();
    const fromDate = new Date(currentFrom);
    const toDate = new Date(currentTo);

    return this.marketingApi.list().pipe(
      switchMap(allCampaigns => {
        // Solo campañas del business actual
        const campaigns = allCampaigns.filter(c => c.businessId === params.businessId);

        // Contar campañas por estado (todas, no solo del mes)
        const campaignsByStatus = this.countCampaignsByStatus(campaigns);

        // Filtrar enviadas del mes actual para el funnel
        const sentThisMonth = campaigns.filter(c => {
          if (c.status !== 'SENT') return false;
          if (!c.sentAt) return false;
          const sentDate = new Date(c.sentAt);
          return sentDate >= fromDate && sentDate <= toDate;
        });

        if (sentThisMonth.length === 0) {
          return of(this.buildEmptyEmailFunnel(campaignsByStatus));
        }

        // Traer las métricas de cada campaña en paralelo
        const metricsRequests = sentThisMonth.map(c =>
          this.marketingApi.getMetrics(c.id).pipe(
            catchError(() => of(null as CampaignMetrics | null))
          )
        );

        return forkJoin(metricsRequests).pipe(
          map(allMetrics => this.buildEmailFunnelFromMetrics(
            allMetrics,
            sentThisMonth.length,
            campaignsByStatus
          ))
        );
      }),
      catchError(err => {
        console.warn('[Dashboard] error al cargar embudo de correos — widget vacío', err);
        return of(this.buildEmptyEmailFunnel(this.zeroCampaignsByStatus()));
      })
    );
  }

  /** Cuenta campañas por estado. */
  private countCampaignsByStatus(campaigns: MarketingCampaign[]): EmailCampaignsByStatus {
    const counts = this.zeroCampaignsByStatus();
    for (const c of campaigns) {
      counts.total++;
      switch (c.status) {
        case 'DRAFT':     counts.draft++;     break;
        case 'SCHEDULED': counts.scheduled++; break;
        case 'SENDING':   counts.sending++;   break;
        case 'SENT':      counts.sent++;      break;
        case 'CANCELLED': counts.cancelled++; break;
        case 'FAILED':    counts.failed++;    break;
      }
    }
    return counts;
  }

  private zeroCampaignsByStatus(): EmailCampaignsByStatus {
    return { draft: 0, scheduled: 0, sending: 0, sent: 0, cancelled: 0, failed: 0, total: 0 };
  }

  /** Suma métricas de múltiples campañas y arma el funnel de 4 etapas + métricas. */
  private buildEmailFunnelFromMetrics(
    allMetrics: (CampaignMetrics | null)[],
    campaignsThisMonth: number,
    campaignsByStatus: EmailCampaignsByStatus
  ): EmailFunnel {
    let totalRecipients = 0;
    let totalSent       = 0;
    let totalDelivered  = 0;
    let totalOpened     = 0;
    let totalClicked    = 0;
    let totalBounced    = 0;
    let totalFailed     = 0;

    for (const m of allMetrics) {
      if (!m) continue;
      totalRecipients += m.totalRecipients ?? 0;
      totalSent       += m.sent ?? 0;
      totalDelivered  += m.delivered ?? 0;
      totalOpened     += m.opens?.unique ?? 0;
      totalClicked    += m.clicks?.unique ?? 0;
      totalBounced    += m.bounced ?? 0;
      totalFailed     += m.failed ?? 0;
    }

    const pct = (num: number, den: number) =>
      den > 0 ? +((num / den) * 100).toFixed(1) : 0;

    // % vs etapa anterior y vs total (destinatarios)
    const stages: EmailFunnelStage[] = [
      {
        stage: 'recipients',
        label: 'Destinatarios',
        count: totalRecipients,
        percentFromPrevious: 100,
        percentFromTotal: 100
      },
      {
        stage: 'sent',
        label: 'Enviados',
        count: totalSent,
        percentFromPrevious: pct(totalSent, totalRecipients),
        percentFromTotal: pct(totalSent, totalRecipients)
      },
      {
        stage: 'delivered',
        label: 'Entregados',
        count: totalDelivered,
        percentFromPrevious: pct(totalDelivered, totalSent),
        percentFromTotal: pct(totalDelivered, totalRecipients)
      },
      {
        stage: 'viewed',
        label: 'Visualizados',
        count: totalOpened,
        percentFromPrevious: pct(totalOpened, totalDelivered),
        percentFromTotal: pct(totalOpened, totalRecipients)
      }
    ];

    const metrics: EmailKeyMetrics = {
      openRate: totalDelivered > 0 ? +((totalOpened / totalDelivered) * 100).toFixed(1) : null,
      ctor: totalOpened > 0 ? +((totalClicked / totalOpened) * 100).toFixed(1) : null,
      bounces: totalBounced,
      failed: totalFailed
    };

    return { campaignsThisMonth, campaignsByStatus, stages, metrics };
  }

  /** Estructura vacía cuando no hay campañas enviadas en el mes. */
  private buildEmptyEmailFunnel(campaignsByStatus: EmailCampaignsByStatus): EmailFunnel {
    return {
      campaignsThisMonth: 0,
      campaignsByStatus,
      stages: [
        { stage: 'recipients', label: 'Destinatarios', count: 0, percentFromPrevious: 0, percentFromTotal: 0 },
        { stage: 'sent',       label: 'Enviados',      count: 0, percentFromPrevious: 0, percentFromTotal: 0 },
        { stage: 'delivered',  label: 'Entregados',    count: 0, percentFromPrevious: 0, percentFromTotal: 0 },
        { stage: 'viewed',     label: 'Visualizados',  count: 0, percentFromPrevious: 0, percentFromTotal: 0 }
      ],
      metrics: { openRate: null, ctor: null, bounces: 0, failed: 0 }
    };
  }

  // ============================================================
  // Reuniones (RF-XX) — Pivot Table
  // GET /api/meetings + /api/meeting-types + /api/meeting-statuses
  // ============================================================
  getMeetingsPivot(params: DashboardParams): Observable<MeetingsPivot> {
    return forkJoin({
      meetings: this.fetchAllMeetings(params.businessId),
      types: this.fetchMeetingTypes(params.businessId),
      statuses: this.fetchMeetingStatuses(params.businessId)
    }).pipe(
      switchMap(({ meetings, types, statuses }) => {
        const contactIds = Array.from(new Set(
          meetings.map(m => m.contactId).filter((id): id is number => id != null)
        ));
        const companyIds = Array.from(new Set(
          meetings.map(m => m.companyId).filter((id): id is number => id != null)
        ));

        return forkJoin({
          meetings: of(meetings),
          types: of(types),
          statuses: of(statuses),
          contactNames: this.fetchContactNames(params.businessId, contactIds),
          companyNames: this.fetchCompanyNames(params.businessId, companyIds)
        });
      }),
      map(({ meetings, types, statuses, contactNames, companyNames }) =>
        this.buildMeetingsPivot(meetings, types, statuses, contactNames, companyNames)
      ),
      catchError(err => {
        console.warn('[Dashboard] error al cargar reuniones — widget vacío', err);
        return of(this.buildMeetingsPivot([], [], [], new Map(), new Map()));
      })
    );
  }

  //
  /** Agrupa tickets por status del backend en los 3 buckets del widget. */
  /** Combina lista de tickets (para por estado) + dashboard agregado (para KPIs). */
  private buildTicketsByStatus(
    tickets: any[],
    dashboard: TicketMainDashboardResponse | null
  ): TicketsByStatus {
    // Clasificación flexible por status (mantiene regex)
    const isOpen = (s: string) => /open|abierto|nuevo|new/i.test(s);
    const isInProgress = (s: string) => /progress|proceso|asignado|assigned/i.test(s);
    const isClosed = (s: string) => /closed|cerrado|resolved|resuelto/i.test(s);

    let open = 0, inProgress = 0, closed = 0;
    for (const t of tickets) {
      const status = (t.status ?? '').toString();
      if (isClosed(status))         closed++;
      else if (isInProgress(status)) inProgress++;
      else if (isOpen(status))       open++;
      else                           open++;
    }

    const total = open + inProgress + closed;
    const pct = (n: number) => total > 0 ? +((n / total) * 100).toFixed(1) : 0;

    return {
      total,
      open,
      inProgress,
      closed,
      openPercent: pct(open),
      inProgressPercent: pct(inProgress),
      closedPercent: pct(closed),
      // KPIs del dashboard (con fallback a 0 / null si el endpoint no respondió)
      createdToday: dashboard?.createdToday ?? 0,
      resolvedToday: dashboard?.resolvedToday ?? 0,
      slaAtRisk: dashboard?.slaAtRisk ?? 0,
      slaBreached: dashboard?.slaBreached ?? 0,
      avgFirstResponseMin: dashboard?.avgFirstResponseMin ?? null,
      avgResolutionMin: dashboard?.avgResolutionMin ?? null,
      csatAvg: dashboard?.csatAvg ?? null
    };
  }

  
  /** Agrupa deals según las etapas REALES del CRM (dinámicas). */
  private buildBusinessesByStatus(
    cycles: DealCycleResponse[],
    deals: DealResponse[]
  ): BusinessesByStatus {
    const wonIds = this.identifyWonCycleIds(cycles);
    const lostIds = this.identifyLostCycleIds(cycles);

    // Acumular count y amount por idDealCycle
    const accum = new Map<number, { count: number; amount: number }>();
    for (const d of deals) {
      const cycleId = d.idDealCycle;
      if (cycleId == null) continue;
      const a = accum.get(cycleId) ?? { count: 0, amount: 0 };
      a.count += 1;
      a.amount += this.dealAmount(d);
      accum.set(cycleId, a);
    }

    // Construir una rebanada por CADA etapa del pipeline (ordenadas por order)
    const sortedCycles = [...cycles].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const slices: BusinessStatusSlice[] = sortedCycles.map((c, idx) => {
      const data = accum.get(c.id) ?? { count: 0, amount: 0 };
      const isWon = wonIds.has(c.id);
      const isLost = lostIds.has(c.id);
      return {
        stageId: c.id,
        stageName: c.name,
        stageOrder: c.order ?? idx,
        isWon,
        isLost,
        count: data.count,
        amount: data.amount,
        percent: 0,                            // se completa abajo
        color: this.colorForCycle(idx, isWon, isLost)
      };
    });

    const total = slices.reduce((s, x) => s + x.count, 0);
    const totalAmount = slices.reduce((s, x) => s + x.amount, 0);
    for (const s of slices) {
      s.percent = total > 0 ? +((s.count / total) * 100).toFixed(1) : 0;
    }

    return { total, totalAmount, slices };
  }

  /** Asigna un color al cycle: ganado=verde, perdido=rojo, resto=paleta cíclica. */
  private colorForCycle(index: number, isWon: boolean, isLost: boolean): string {
    if (isWon)  return '#10b981';   // emerald-500 — Ganados
    if (isLost) return '#ef4444';   // red-500 — Perdidos

    // Paleta para etapas activas (excluye verde y rojo)
    const palette = [
      '#3b82f6',   // blue-500
      '#8b5cf6',   // violet-500
      '#f59e0b',   // amber-500
      '#06b6d4',   // cyan-500
      '#ec4899',   // pink-500
      '#84cc16',   // lime-500
      '#a855f7',   // purple-500
      '#0ea5e9',   // sky-500
      '#f97316',   // orange-500
    ];
    return palette[index % palette.length];
  }

  /** Construye pivot tipo × estado, más top contactos y empresas. */
  private buildMeetingsPivot(
    meetings: any[],
    types: any[],
    statuses: any[],
    contactNames: Map<number, string>,
    companyNames: Map<number, string>
  ): MeetingsPivot {
    // Clasificar estados en 3 buckets por nombre
    const statusById = new Map<number, any>(statuses.map(s => [s.id, s]));
    const statusBucket = (idStatus: number): 'scheduled' | 'completed' | 'cancelled' => {
      const s = statusById.get(idStatus);
      const text = `${s?.name ?? ''} ${s?.label ?? ''}`.toLowerCase();
      if (/cancel/.test(text)) return 'cancelled';
      if (/complet|realiz|finaliz|done/.test(text)) return 'completed';
      return 'scheduled';
    };

    // Agrupar por tipoId real
    const typeById = new Map<number, any>(types.map(t => [t.id, t]));
    const rowsByType = new Map<number, MeetingsPivotRow>();
    const byContactCount = new Map<number, number>();
    const byCompanyCount = new Map<number, number>();

    for (const m of meetings) {
      const typeId = m.idMeetingType;
      if (typeId == null) continue;

      let row = rowsByType.get(typeId);
      if (!row) {
        const t = typeById.get(typeId);
        row = {
          typeId,
          typeName: t?.name ?? `Tipo ${typeId}`,
          scheduled: 0,
          completed: 0,
          cancelled: 0,
          total: 0
        };
        rowsByType.set(typeId, row);
      }

      const bucket = statusBucket(m.idMeetingStatus);
      row[bucket]++;
      row.total++;

      if (m.contactId) byContactCount.set(m.contactId, (byContactCount.get(m.contactId) ?? 0) + 1);
      if (m.companyId) byCompanyCount.set(m.companyId, (byCompanyCount.get(m.companyId) ?? 0) + 1);
    }

    const rows = Array.from(rowsByType.values())
      .sort((a, b) => a.typeName.localeCompare(b.typeName));

    const totals = {
      scheduled: rows.reduce((s, r) => s + r.scheduled, 0),
      completed: rows.reduce((s, r) => s + r.completed, 0),
      cancelled: rows.reduce((s, r) => s + r.cancelled, 0),
      grandTotal: rows.reduce((s, r) => s + r.total, 0)
    };

    const topItems = (countMap: Map<number, number>, nameMap: Map<number, string>): MeetingsTopItem[] =>
      Array.from(countMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([id, count]) => ({
          id,
          name: nameMap.get(id) ?? `ID ${id}`,
          count
        }));

    return {
      rows,
      totals,
      byContact: topItems(byContactCount, contactNames),
      byCompany: topItems(byCompanyCount, companyNames)
    };
  }

  /** Trae los nombres de los contactos por sus IDs (una sola llamada paginada). */
  private fetchContactNames(businessId: number, ids: number[]): Observable<Map<number, string>> {
    if (ids.length === 0) return of(new Map());

    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '1000');
    return this.http.get<PageResponse<any>>(`${this.apiUrl}/contacts`, { params }).pipe(
      map(r => {
        const map = new Map<number, string>();
        const idSet = new Set(ids);
        for (const c of (r.content ?? [])) {
          if (!idSet.has(c.id)) continue;
          // Backend usa: fullname + lastname (no name/lastName)
          const name = [c.fullname, c.lastname].filter(Boolean).join(' ').trim()
                    || c.mail
                    || `Contacto ${c.id}`;
          map.set(c.id, name);
        }
        return map;
      }),
      catchError(() => of(new Map<number, string>()))
    );
  }

  /** Trae los nombres de las empresas por sus IDs. */
  private fetchCompanyNames(businessId: number, ids: number[]): Observable<Map<number, string>> {
    if (ids.length === 0) return of(new Map());

    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '1000');
    return this.http.get<PageResponse<any>>(`${this.apiUrl}/companies`, { params }).pipe(
      map(r => {
        const map = new Map<number, string>();
        const idSet = new Set(ids);
        for (const c of (r.content ?? [])) {
          if (!idSet.has(c.id)) continue;
          // Backend usa: commercialName
          map.set(c.id, c.commercialName ?? c.name ?? `Empresa ${c.id}`);
        }
        return map;
      }),
      catchError(() => of(new Map<number, string>()))
    );
  }

  //
  // FETCHERS — llamadas HTTP a endpoints del backend
  //

  private fetchAllMeetings(businessId: number): Observable<any[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '500');
    return this.http.get<PageResponse<any>>(`${this.apiUrl}/meetings`, { params })
      .pipe(
        map(r => r.content ?? []),
        catchError(() => of([] as any[]))
      );
  }

   private fetchMeetingTypes(businessId: number): Observable<any[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<any>(`${this.apiUrl}/meeting-types/active`, { params }).pipe(
      map(r => Array.isArray(r) ? r : (r?.content ?? [])),
      catchError(() => of([] as any[]))
    );
  }

  private fetchMeetingStatuses(businessId: number): Observable<any[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<any>(`${this.apiUrl}/meeting-statuses/active`, { params }).pipe(
      map(r => Array.isArray(r) ? r : (r?.content ?? [])),
      catchError(() => of([] as any[]))
    );
  }

  //
  // MOCK — solo para Correo
  //
   private mockEmailFunnel(businessId: number): EmailFunnel {
    const recipients = 120;
    const sent = 115;
    const delivered = 108;
    const opened = 42;
    const clicked = 9;
    return {
      campaignsThisMonth: 3,
      campaignsByStatus: { draft: 3, scheduled: 0, sending: 0, sent: 14, cancelled: 2, failed: 0, total: 19 },
      stages: [
        { stage: 'recipients', label: 'Destinatarios', count: recipients, percentFromPrevious: 100,                                                      percentFromTotal: 100 },
        { stage: 'sent',       label: 'Enviados',      count: sent,       percentFromPrevious: +((sent / recipients) * 100).toFixed(1),                  percentFromTotal: +((sent / recipients) * 100).toFixed(1) },
        { stage: 'delivered',  label: 'Entregados',    count: delivered,  percentFromPrevious: +((delivered / sent) * 100).toFixed(1),                   percentFromTotal: +((delivered / recipients) * 100).toFixed(1) },
        { stage: 'viewed',     label: 'Visualizados',  count: opened,     percentFromPrevious: +((opened / delivered) * 100).toFixed(1),                 percentFromTotal: +((opened / recipients) * 100).toFixed(1) }
      ],
      metrics: {
        openRate: +((opened / delivered) * 100).toFixed(1),
        ctor: +((clicked / opened) * 100).toFixed(1),
        bounces: 7,
        failed: 5
      }
    };
  }
}